import React from 'react';
import { Box, IconButton, Tooltip, Typography, Popover } from '@mui/material';
import { CheckCircle, Build, Edit } from '@mui/icons-material';
import { colors } from '../../../themes/typography';
import InlineDatePicker from './InlineDatePicker';
import ResolutionFlow from './ResolutionFlow';
import SelectionContext from '../../../contexts/SelectionContext';
import dayjs from 'dayjs';
import { ConfirmationPopover } from '../../TechnicalAlertsTable/PopoverSystem';
import CreateExperimentForm from '../../shared/CreateExperimentForm';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import axios from 'axios';

/**
 * AlertActions component - Optimized 3-step flow with single API call
 * Phase 4b: Integrated ResolutionFlow for complete workflow
 */
const AlertActions = ({ 
  alert,
  onStatusChange,
  loading = false,
  t
}) => {
  // Get selection context - will be null if not in a selection provider
  const selectionContext = React.useContext(SelectionContext);
  
  const {
    selectedIds = new Set(),
    selectedCount = 0, 
    isMultiSelect = false,
    onBulkAlertAction,
    onSingleAlertAction
  } = selectionContext || {};
  // Phase 4b: State for 3-step flow (no API calls until final step)
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const [showResolutionFlow, setShowResolutionFlow] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(dayjs().add(1, 'day')); // Default to tomorrow
  // Initialize plannedDate with alert's resolution_date if already under review
  const [plannedDate, setPlannedDate] = React.useState(() => {
    return alert.status === 'under_review' && alert.resolution_date 
      ? dayjs(alert.resolution_date) 
      : null;
  });
  const [dateLoading, setDateLoading] = React.useState(false);
  // New state: confirmation and experiment creation popovers
  const [confirmAnchorEl, setConfirmAnchorEl] = React.useState(null);
  const [createAnchorEl, setCreateAnchorEl] = React.useState(null);
  const lastAlertIdRef = React.useRef(null);


  // Update plannedDate when alert changes (data refresh)
  React.useEffect(() => {
    if (alert.status === 'under_review' && alert.resolution_date) {
      setPlannedDate(dayjs(alert.resolution_date));
    } else if (alert.status !== 'under_review') {
      setPlannedDate(null);
    }
  }, [alert.resolution_date, alert.status]);

  const handleNotBroken = (event) => {
    // Check if we're in a selection context and have multiple selected
    if (selectionContext && isMultiSelect) {
      // Bulk action on all selected alerts
      const selectedIdArray = Array.from(selectedIds);
      if (onBulkAlertAction) {
        onBulkAlertAction(selectedIdArray, 'not_broken');
      }
    } else if (selectionContext && onSingleAlertAction) {
      // Single action: open confirmation popover to optionally create experiment
      setConfirmAnchorEl(event.currentTarget);
    } else if (onStatusChange) {
      setConfirmAnchorEl(event.currentTarget);
    }
  };

  const handleBroken = () => {
    setShowDatePicker(true);
  };

  const handleDateSubmit = async () => {
    setDateLoading(true);
    
    try {
      const metadata = {
        planned_resolution_date: selectedDate.format('YYYY-MM-DD')
      };
      
      
      // Check if we're in a selection context and have multiple selected
      if (selectionContext && isMultiSelect) {
        // Bulk action on all selected alerts
        const selectedIdArray = Array.from(selectedIds);
        if (onBulkAlertAction) {
          await onBulkAlertAction(selectedIdArray, 'mark_as_broken', metadata);
          // For bulk operations, close the date picker but don't show resolution flow
          setShowDatePicker(false);
          setSelectedDate(dayjs().add(1, 'day')); // Reset date
        }
      } else {
        // Single alert action
        if (selectionContext && onSingleAlertAction) {
          await onSingleAlertAction(alert.id, 'mark_as_broken', metadata);
        } else if (onStatusChange) {
          await onStatusChange('mark_as_broken', metadata);
        }
        // Store date locally and move to resolution flow for single alert
        setPlannedDate(selectedDate);
        setShowDatePicker(false);
        setShowResolutionFlow(true);
      }
    } catch (error) {
      console.error('Failed to mark as broken:', error);
    } finally {
      setDateLoading(false);
    }
  };

  const handleEditDate = (newDate) => {
    setPlannedDate(newDate);
  };

  const handleMarkRepaired = async (finalDate) => {
    setDateLoading(true);
    
    try {
      const metadata = {
        resolution_date: finalDate.format('YYYY-MM-DD')
      };
      
      // Check if we're in a selection context and have multiple selected
      if (selectionContext && isMultiSelect) {
        // Bulk action on all selected alerts
        const selectedIdArray = Array.from(selectedIds);
        if (onBulkAlertAction) {
          await onBulkAlertAction(selectedIdArray, 'resolved', metadata);
        }
      } else {
        // Single alert action
        if (selectionContext && onSingleAlertAction) {
          await onSingleAlertAction(alert.id, 'resolved', metadata);
        } else if (onStatusChange) {
          await onStatusChange('resolved', metadata);
        }
      }
      
      // Reset all states
      setShowResolutionFlow(false);
      setPlannedDate(null);
      setSelectedDate(dayjs().add(1, 'day'));
    } catch (error) {
      console.error('Resolution failed:', error);
    } finally {
      setDateLoading(false);
    }
  };

  const handleDateCancel = () => {
    setShowDatePicker(false);
    setSelectedDate(dayjs().add(1, 'day')); // Reset to default
  };

  const handleResolutionCancel = () => {
    setShowResolutionFlow(false);
    setPlannedDate(null);
    setSelectedDate(dayjs().add(1, 'day'));
  };

  // Render based on alert status field
  const alertStatus = alert.status || 'pending';

  // Not broken alerts should never appear (filtered by backend)
  if (alertStatus === 'not_apply') {
    return null;
  }

  if (alertStatus === 'resolved') {
    // Resolved state - show completion status
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: '4px'
      }}>
        <Typography sx={{
          fontSize: '9px',
          color: '#34C759', // Apple green
          fontWeight: 600,
          textAlign: 'center',
          px: '8px',
          py: '2px',
          borderRadius: '6px', // Slightly more rounded for Apple feel
          backgroundColor: 'rgba(52, 199, 89, 0.08)', // Apple green background
          border: '1px solid rgba(52, 199, 89, 0.12)'
        }}>
          {t ? t('resolved') : 'Resolved'} • {dayjs(alert.resolution_date).format('MMM DD')}
        </Typography>
      </Box>
    );
  }

  if (alertStatus === 'under_review') {
    // Under review - show resolution flow
    // Use local plannedDate if it exists (after editing), otherwise use alert's resolution_date
    const displayDate = plannedDate || (alert.resolution_date ? dayjs(alert.resolution_date) : null);
    
    return (
      <ResolutionFlow
        scheduledDate={displayDate}
        onEditDate={handleEditDate}
        onMarkRepaired={handleMarkRepaired}
        loading={dateLoading}
        t={t}
      />
    );
  }

  // Phase 4b: Show resolution flow after date planning
  if (showResolutionFlow) {
    return (
      <ResolutionFlow
        scheduledDate={plannedDate}
        onEditDate={handleEditDate}
        onMarkRepaired={handleMarkRepaired}
        loading={dateLoading}
        t={t}
      />
    );
  }

  // Phase 4b: Show date picker inline if activated
  if (showDatePicker) {
    return (
      <InlineDatePicker
        selectedDate={selectedDate}
        onChange={setSelectedDate}
        onSubmit={handleDateSubmit}
        onCancel={handleDateCancel}
        loading={dateLoading}
        t={t}
      />
    );
  }

  // Default: pending status - show question text and buttons inline
  return (
    <Box sx={{
      display: 'flex',
      gap: '8px',
      justifyContent: 'center',
      alignItems: 'center',
      py: '8px'
    }}>
      {/* Question text inline with buttons */}
      <Typography sx={{
        fontSize: '10px',
        color: colors.text.primary,
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap'
      }}>
        {t ? t('isThisBroken') : 'Is this slot broken?'}
      </Typography>
        <Tooltip title={t ? t('markAsNotBroken') : 'Mark as working'} arrow>
        <IconButton
          onClick={handleNotBroken}
          disabled={loading}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like experiment cards
            borderRadius: '4px', // Smaller radius
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          <CheckCircle sx={{ 
            fontSize: '12px', 
            color: '#8E8E93' // Apple gray
          }} />
        </IconButton>
      </Tooltip>

      <Tooltip title={t ? t('markAsBroken') : 'Report issue'} arrow>
        <IconButton
          onClick={handleBroken}
          disabled={loading}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like experiment cards
            borderRadius: '4px', // Smaller radius
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(249, 115, 22, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          <Build sx={{ 
            fontSize: '12px', 
          color: '#8E8E93' // Apple gray
          }} />
        </IconButton>
      </Tooltip>

      {/* Confirmation: Create experiment? */}
      <ConfirmationPopover
        open={Boolean(confirmAnchorEl)}
        anchorEl={confirmAnchorEl}
        onClose={() => setConfirmAnchorEl(null)}
        title="createExperimentQuestion"
        onConfirm={() => {
          setCreateAnchorEl(confirmAnchorEl);
          setConfirmAnchorEl(null);
        }}
        onCancel={async () => {
          // Proceed with normal flow: mark not broken and refresh
          setConfirmAnchorEl(null);
          if (selectionContext && onSingleAlertAction) {
            await onSingleAlertAction(alert.id, 'not_broken');
          } else if (onStatusChange) {
            await onStatusChange('not_broken');
          }
        }}
        confirmText="yes"
        cancelText="no"
      />

      {/* Create Experiment popover */}
      <Popover
        open={Boolean(createAnchorEl)}
        anchorEl={createAnchorEl}
        onClose={() => setCreateAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: '12px',
            border: `1px solid ${colors.border.light}`,
            boxShadow: `0 8px 32px rgba(0,0,0,0.08)`,
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            mt: '8px'
          }
        }}
      >
        <CreateExperimentForm
          open={Boolean(createAnchorEl)}
          column={{ id: 'pending' }}
          currentFilters={{}}
          filterOptions={{ groups: [] }}
          clientId={selectionContext?.clientId}
          allowPriceIncrease={false}
          onClose={() => setCreateAnchorEl(null)}
          onSuccess={async () => {
            // Refresh table after experiment creation
            if (selectionContext?.onDataRefresh) {
              await selectionContext.onDataRefresh();
            }
            setCreateAnchorEl(null);
          }}
          onError={() => {}}
          t={t}
          beforeSubmit={async () => {
            // First, mark alert as not broken and capture returned ID
            const token = localStorage.getItem('access_token');
            const resp = await axios.post(
              `${API_BASE_URL}/alerts/update?clientId=${selectionContext?.clientId}`,
              { alertIds: [alert.id], action: 'confirm_not_broken', metadata: {} },
              { headers: createAuthHeader(token) }
            );
            const succ = resp.data?.results?.succeeded || [];
            lastAlertIdRef.current = (succ[0]?.alertId) ?? alert.id;
          }}
          extraRequestBody={{ alertId: lastAlertIdRef.current ?? alert.id }}
          initialValues={{
            dStoreId: alert.d_store_id || alert.store_id || alert.machine_id,
            dProductId: alert.d_product_id,
            productName: alert.product_name,
            machineDisplayName: `${alert.group || ''}${alert.group ? ' - ' : ''}${alert.location || ''}${(alert.machine_id || alert.store_id) ? ` (${alert.machine_id || alert.store_id})` : ''}`.trim()
          }}
          lockMachine
          lockProduct
          presetSelection={{
            dStoreId: alert.d_store_id || alert.store_id || alert.machine_id,
            dProductId: alert.d_product_id,
            machineLabel: `${alert.group || ''}${alert.group ? ' - ' : ''}${alert.location || ''}${(alert.machine_id || alert.store_id) ? ` (${alert.machine_id || alert.store_id})` : ''}`.trim(),
            productLabel: alert.product_name
          }}
        />
      </Popover>
    </Box>
  );
};

export default AlertActions;
